import { useEffect, useRef, useState, type FormEvent } from 'react';
import { get, post, ApiError, type CrmLookup, type FollowUp } from '../lib/api.ts';
import { CHANNELS, RESULTS, STATUSES, PRIORITIES, NEXT_REQUIRED_MSG, noNextNeeded , syncCompanyCustomer } from '../lib/labels.ts';
import { addDays, cairoToday, cairoWorkDay, shortDate } from '../lib/format.ts';
import { DateInput, Alert, Field, Select, TextArea, TextInput } from '../components/ui.tsx';
import { AltPhoneField } from '../components/AltPhoneField.tsx';

/**
 * **والبنود الأربعة تبدأ فارغة** (قرار المالك: «خلّيهم فاضيين علشان هو يملاها ومش
 * يكسل ويسيبهم زي ما هو»): قيمةٌ جاهزة في خانةٍ تصف الواقع تُترك كما هي، فيصير
 * «مكالمة · تم الرد · مهتم» وصفَ كل متابعةٍ في النظام — بيانٌ لا يُقرأ منه شيء.
 * واليوم يبقى جاهزًا: هو حقيقةُ اللحظة لا رأيًا يُبديه أحد.
 *
 * **باليوم فقط — بلا ساعة** (قرار المالك 01/10/2026: «إن البياع يسجّل توقيت المتابعة… رخم، خلّيه تسجيل المتابعة
 * باليوم فقط وشيل ساعة التواصل دي، وخلّي البرنامج يفكّره… 3 أو 4 تنبيهات»): لا خانة «وقت التواصل» (الخادم يسجّل
 * ساعة الحفظ وحده) ولا «وقت المتابعة القادمة» — والتذكير صار أربع مرّات في اليوم لكل ما يستحقّ النهاردة (0149).
 */
const EMPTY = {
  customerName: '', phone: '', altPhone: '', contactedOn: cairoToday(),
  channel: '', result: '', customerStatus: '',
  notes: '', nextFollowUpOn: '', priority: '',
};

/** تسجيل سريع: اليوم والوقت جاهزان، والبنود يملؤها صاحبها. */
export function QuickAddForm({ onAdded, prefill }: {
  onAdded: (f: FollowUp) => void;
  /** من «بنك الأرقام» (0104): الاسم والرقم وآخر طلب — فلا يُكتب رقمٌ بيده مرّتين */
  prefill?: { customerName: string; phone: string; notes: string; key: number } | null;
}) {
  // لحظةُ الفتح لا لحظةُ تحميل الملف: EMPTY ثابتٌ حُسب مرّةً عند الإقلاع، والهاتف يبقى مفتوحًا أيامًا (مراجعة 27/09/2026)
  const [form, setForm] = useState(() => ({ ...EMPTY, contactedOn: cairoToday() }));
  useEffect(() => {
    if (!prefill) return;
    setForm(f => ({ ...f, customerName: prefill.customerName, phone: prefill.phone, notes: prefill.notes,
                     contactedOn: cairoToday() }));
    setSaved(null); setError(null);
    nameRef.current?.focus();
  }, [prefill]);
  const [lookup, setLookup] = useState<CrmLookup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  const set = (k: keyof typeof EMPTY, v: string) => setForm(f => ({ ...f, [k]: v }));

  // كشف الرقم تلقائيًا بعد توقف الكتابة (مساعدة فقط — لا يغيّر بيانات العميل)
  useEffect(() => {
    const digits = form.phone.replace(/\D/g, '');
    if (digits.length < 8) { setLookup(null); return; }
    const t = setTimeout(() => {
      get<CrmLookup>(`/crm/lookup?phone=${encodeURIComponent(form.phone)}`).then(setLookup).catch(() => setLookup(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.phone]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null); setSaved(null);
    if (!form.nextFollowUpOn && !noNextNeeded(form.customerStatus, form.result)) { setError(NEXT_REQUIRED_MSG); return; }
    setBusy(true);
    try {
      const body = {
        customerName: form.customerName.trim(), phone: form.phone.trim(), altPhone: form.altPhone.trim() || null,
        channel: form.channel, result: form.result, customerStatus: form.customerStatus,
        notes: form.notes.trim() || null,
        nextFollowUpOn: form.nextFollowUpOn || null,
        priority: form.priority,
      };
      const created = await post<FollowUp>('/follow-ups', body);
      onAdded(created);
      setSaved(`تم تسجيل المتابعة ${created.followUpNo}`);
      setForm({ ...EMPTY, contactedOn: cairoToday() });
      setLookup(null);
      nameRef.current?.focus();                       // جاهز للعميل التالي فورًا
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'تعذّر حفظ المتابعة');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} aria-label="تسجيل متابعة" className="space-y-3">
      {error && <Alert>{error}</Alert>}
      {saved && <Alert kind="success">{saved}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="اسم العميل" required>
          <TextInput ref={nameRef} name="customerName" required maxLength={150} value={form.customerName}
            onChange={e => set('customerName', e.target.value)} placeholder="مثال: أحمد سمير" />
        </Field>
        <Field label="رقم الهاتف" required hint="مصري أو دولي — يُحفظ بصيغة دولية">
          <TextInput name="phone" required inputMode="tel" maxLength={30} value={form.phone}
            onChange={e => set('phone', e.target.value)} placeholder="01xxxxxxxxx" />
        </Field>
        <AltPhoneField key={form.phone === '' ? 'empty' : 'filled'} value={form.altPhone} onChange={v => set('altPhone', v)} />
        {/* لا اختيار لتاريخ التواصل (0158، قرار المالك 02/10/2026: «البياع يسجّل في يومه فقط»): البرنامج يسجّله بيوم الشغل */}
        <Field label="تاريخ التواصل" hint="بيتسجّل لوحده بيوم الشغل الحالي">
          <TextInput name="contactedOn" value={shortDate(cairoWorkDay())} disabled readOnly className="tabular-nums" />
        </Field>
      </div>

      {lookup && (
        <div className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-700" role="status">
          {lookup.customer.ownedByOther && lookup.customer.notice
            ? <b className="text-amber-800">{lookup.customer.notice}</b>
            : lookup.customer.exists
            ? <>عميل مسجّل: <b>{lookup.customer.previousName}</b> · عدد الحجوزات السابقة: {lookup.customer.bookingsCount} · آخر حجز: {shortDate(lookup.customer.lastBookingDate)}</>
            : <>رقم جديد — لا توجد حجوزات سابقة</>}
          {lookup.followUps.count > 0 && <> · متابعات سابقة: {lookup.followUps.count} (آخرها {shortDate(lookup.followUps.last?.contactedOn)})</>}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {/* كلّها تبدأ فارغةً بـ«اختر…» — والمتصفّح يمنع الإرسال قبل الاختيار */}
        <Field label="نوع التواصل" required>
          <Select name="channel" required placeholder="اختر…" options={CHANNELS} value={form.channel} onChange={e => set('channel', e.target.value)} />
        </Field>
        <Field label="نتيجة التواصل" required hint={form.result === 'NO_ANSWER' || form.result === 'UNREACHABLE' ? 'لو الرقم من بنك الأرقام: مش هيتحسب في التارجت غير لما العميل يرد عليك (02/10/2026)' : undefined}>
          <Select name="result" required placeholder="اختر…" options={RESULTS} value={form.result} onChange={e => { const v = e.target.value; setForm(cur => syncCompanyCustomer(cur, 'result', v)); }} />
        </Field>
        <Field label="موقف العميل" required>
          <Select name="customerStatus" required placeholder="اختر…" options={STATUSES} value={form.customerStatus} onChange={e => { const v = e.target.value; setForm(cur => syncCompanyCustomer(cur, 'customerStatus', v)); }} />
        </Field>
        <Field label="الأولوية" required>
          <Select name="priority" required placeholder="اختر…" options={PRIORITIES} value={form.priority} onChange={e => set('priority', e.target.value)} />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <Field label="ملاحظات">
            <TextArea name="notes" rows={2} maxLength={2000} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="ملخص المكالمة وطلب العميل" />
          </Field>
        </div>
        <Field label="يوم المتابعة القادمة" required={!noNextNeeded(form.customerStatus, form.result)}
          hint={noNextNeeded(form.customerStatus, form.result) ? 'مش لازم — العميل غير مهتم أو فقدناه' : 'لازم — والبرنامج هيفكّرك بيها في اليوم ده 4 مرّات'}>
          <DateInput name="nextFollowUpOn" required={!noNextNeeded(form.customerStatus, form.result)} min={addDays(cairoToday(), 1)} value={form.nextFollowUpOn} onChange={v => set('nextFollowUpOn', v)} />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'جارٍ الحفظ…' : 'حفظ المتابعة'}</button>
        <button type="button" className="btn-ghost" onClick={() => { setForm({ ...EMPTY, contactedOn: cairoToday() }); setLookup(null); setSaved(null); setError(null); }}>
          مسح الحقول
        </button>
      </div>
    </form>
  );
}
